import { Page } from "navigation/types";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { parseRouteParamToStockNumericType } from "data/stock_types";
import SiteHeader from "containers/SiteHeader";
import PageHome from "containers/Home/PageHome";
import PageLogout from "containers/Logout";
import PageBecomeHost from "containers/PageBecomeHost/PageBecomeHost";
import PageLoginHost from "containers/Login/LoginHost";
import { Host } from "data/types/host";
import { useSelector } from "react-redux";
import { selectCurrentHost } from "application/features/host/hostSlice";
import useWindowSize from "application/hooks/useWindowResize";
import FooterNav from "components/FooterNav";
import FooterNavHost from "components/FooterNavHost";
import ListingStayDetailPage from "containers/ListingDetailPages/Stay/StayDetails";
import ListingExperiencesDetailPage from "containers/ListingDetailPages/Experience/ExperienceDetails";
import HolidayPackageDetailPage from "containers/ListingDetailPages/HolidayPackage/HolidayPackageDetails";
import PageLogin from "containers/Login/LoginCommonPage";
import PageLoginClient from "containers/Login/LoginClient";
import PageSignUpClient from "containers/PageSignUpClient/PageSignUpClient";
import { Client } from "data/types/user";
import FooterNavClient from "components/FooterNavClient";
import { selectCurrentLoggedClient } from "application/features/clientAuth/authClientSlice";
import HostProfile from "containers/HostProfile/HostProfile";
import HostPage from "containers/HostPage/HostPage";
import PageBorgo from "containers/PageBorgo/PageBorgo";
import PageAbout from "containers/About/PageAbout";
import PageError404 from "containers/PageError/PageError404";
import PageError500 from "containers/PageError/PageError500";
import PageInReview from "containers/PageInReview/PageInReview";
import PageContact from "containers/PageContact/PageContact";
import PageClientResetPsw from "containers/PageClientResetPsw/PageClientResetPsw";
import PageClientChangePsw from "containers/PageClientChangePsw/PageClientChangePsw";
import PageHostResetPsw from "containers/PageHostResetPsw/PageHostResetPsw";
import PageHostChangePsw from "containers/PageHostChangePsw/PageHostChangePsw";
import BorghiPage from "containers/ListingDetailPages/Borgo";
import SearchPage from "containers/SearchPage/SearchPage";
import PageCloseAccount from "containers/PageCloseAccount/PageCloseAccount";
import UserTermsAndConditions from "containers/TermsAndConditions/UserTermsAndConditions";
import PrivacyPageIubenda from "containers/PrivacyPageIubenda/PrivacyPageIubenda";

/**
 * Guards the SEO-friendly category routes (e.g. /esperienze, /pacchetti-viaggio).
 * A bare `/:categorySlug` would otherwise turn any unknown single-segment URL
 * into a soft-404 search page, so we only render the SearchPage for slugs that
 * map to a real stock type; anything else falls through to /404.
 * Static routes (e.g. /about) still win over this dynamic route in React Router.
 */
const SearchPageRoute = () => {
  const { categorySlug, defaultType } = useParams();
  const raw = categorySlug ?? defaultType;
  if (raw && parseRouteParamToStockNumericType(raw) === undefined) {
    return <Navigate to="/404" replace />;
  }
  return <SearchPage />;
};

const MainPages: Page[] = [
  { path: "/", component: PageHome },
  { path: "/about", component: PageAbout },
  { path: "/contacts", component: PageContact },
  { path: "/404", component: PageError404 },
  { path: "/500", component: PageError500 },
  { path: "/in-review", component: PageInReview },
  // SEO-friendly category search, e.g. /pacchetti-viaggio (localised slug)
  { path: "/:categorySlug", component: SearchPageRoute },
  // Legacy form kept for backward compatibility, e.g. /2/search or /soggiorni/search
  { path: "/:defaultType/search", exact: false, component: SearchPageRoute },

  { path: "/borghi", exact: false, component: BorghiPage },
  { path: "/borgo/:name", component: PageBorgo },
  { path: "/logout", component: PageLogout },
  { path: "/become-host", component: PageBecomeHost },
  { path: "/login", component: PageLogin },
  { path: "/login-host", component: PageLoginHost },
  { path: "/login-client", component: PageLoginClient },
  { path: "/terms-and-conditions", component: UserTermsAndConditions },
  { path: "/privacy-policy", component: PrivacyPageIubenda },
  { path: "/reset-password-client/", component: PageClientResetPsw },
  { path: "/reset-password-client/:token", component: PageClientChangePsw },
  { path: "/reset-password-host/", component: PageHostResetPsw },
  { path: "/reset-password-host/:token", component: PageHostChangePsw },
  { path: "/close-account", component: PageCloseAccount },
  { path: "/profile/:host_uuid", component: HostProfile },
  { path: "/signup-client", component: PageSignUpClient },
  // SEO-friendly detail pages (singular, localised). EN slugs also cover ES.
  { path: "/soggiorno/:id", component: ListingStayDetailPage },
  { path: "/stay/:id", component: ListingStayDetailPage },
  { path: "/esperienza/:id", component: ListingExperiencesDetailPage },
  { path: "/experience/:id", component: ListingExperiencesDetailPage },
  { path: "/pacchetto-viaggio/:id", component: HolidayPackageDetailPage },
  { path: "/holiday-package/:id", component: HolidayPackageDetailPage },
  // Legacy detail routes kept for backward compatibility.
  { path: "/listing-stay-detail/:id", component: ListingStayDetailPage },
  { path: "/experience-detail/:id", component: ListingExperiencesDetailPage },
  { path: "/holiday-package-detail/:id", component: HolidayPackageDetailPage },
  { path: "/author/:host_uuid", component: HostPage },
]

const MainRouter = () => {
  let WIN_WIDTH = useWindowSize().width;
  if (typeof window !== "undefined") {
    WIN_WIDTH = WIN_WIDTH || window.innerWidth;
  }

  const host: Host = useSelector(selectCurrentHost);
  const client: Client = useSelector(selectCurrentLoggedClient);

  return (
    <>
      <SiteHeader navType={client?.client_uuid ? "ClientNav" : "DefaultNav"} />
      <Routes>
        {MainPages.map(({ component, path }) => {
          const Component = component;
          return <Route key={path} element={<Component />} path={path} />;
        })}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
      {WIN_WIDTH < 1024 ?
        host?.host_uuid ?
          <FooterNavHost />
          :
          client?.client_uuid ?
            <FooterNavClient />
            :
            <FooterNav />
        : null
      }
    </>

  )
}

export default MainRouter;